import React, { forwardRef } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { Sparkles } from 'lucide-react';

/**
 * ShareableCard — Obsidian 雜誌風。
 * 支援兩種 aspectRatio: "1:1" (IG Feed) 或 "9:16" (IG Story)
 * 用 forwardRef 讓 parent 直接 html2canvas 截圖
 */
const fmt = (n, d = 1) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  if (typeof n !== 'number') return String(n);
  if (Math.abs(n) >= 1000) return n.toLocaleString();
  return Number(n.toFixed(d));
};

const MiniRetentionChart = ({ curve = [], height = 70 }) => {
  if (!curve || curve.length === 0) return null;
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <AreaChart data={curve} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="retGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FF5436" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#FF5436" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="t_sec" hide />
          <YAxis hide domain={[0, 100]} />
          <Area
            type="monotone"
            dataKey="retention_pct"
            stroke="#FF5436"
            strokeWidth={2}
            fill="url(#retGrad)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

const MiniRadar = ({ scores = {}, size = 160 }) => {
  const data = [
    { k: 'Hook',     v: Number(scores.hook || 0) },
    { k: '留存',     v: Number(scores.retention || 0) },
    { k: '互動',     v: Number(scores.engagement || 0) },
    { k: '觸及',     v: Number(scores.reach || 0) },
    { k: '分享',     v: Number(scores.share_rate || 0) },
  ];
  return (
    <div style={{ width: size, height: size }}>
      <ResponsiveContainer>
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke="rgba(255,255,255,0.15)" />
          <PolarAngleAxis dataKey="k" tick={{ fontSize: 9, fill: '#C8CCD1' }} />
          <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} axisLine={false} />
          <Radar
            dataKey="v"
            stroke="#FF5436"
            strokeWidth={1.5}
            fill="#FF5436"
            fillOpacity={0.35}
            isAnimationActive={false}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

const DemoBar = ({ rows = [] }) => {
  if (!rows || rows.length === 0) return <div className="text-[10px] text-obsidian-400">— 無觀眾數據 —</div>;
  const top = rows.slice(0, 5);
  const max = Math.max(...top.map((r) => r.pct || 0), 1);
  return (
    <div className="space-y-1.5">
      {top.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-10 text-[10px] text-obsidian-300 tabular-nums">{r.region}</div>
          <div className="flex-1 h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
            <div
              className="h-full bg-lime-500 rounded-full"
              style={{ width: `${(r.pct / max) * 100}%` }}
            />
          </div>
          <div className="w-9 text-right text-[10px] text-white tabular-nums">
            {fmt(r.pct, 1)}%
          </div>
        </div>
      ))}
    </div>
  );
};

const ShareableCard = forwardRef(({ report, aspect = '1:1', handle }, ref) => {
  if (!report) return null;
  const r = report || {};
  const v = r.verdict || {};
  const m = r.metrics || {};
  const curve = r.retention_curve || [];
  const demo = r.demographics?.top_locations || [];
  const insights = r.insights || {};
  const scores = r.radar_scores || {};

  const is916 = aspect === '9:16';
  const wrapperStyle = is916
    ? { width: 540, aspectRatio: '9 / 16' }
    : { width: 640, aspectRatio: '1 / 1' };

  return (
    <div
      ref={ref}
      className="relative bg-obsidian-900 text-white overflow-hidden"
      style={{
        ...wrapperStyle,
        fontFamily: '"Noto Serif TC", "PingFang TC", serif',
      }}
      data-testid={`shareable-card-${aspect}`}
    >
      {/* Grid texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      {/* Glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 40% at 50% 0%, rgba(255,84,54,0.08), transparent 60%)',
        }}
      />

      <div className={`relative h-full flex flex-col ${is916 ? 'p-8' : 'p-9'}`}>
        {/* Top kicker */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 bg-lime-500 rounded-[5px] flex items-center justify-center"
              style={{ boxShadow: '0 0 16px rgba(255,84,54,0.4)' }}
            >
              <Sparkles className="w-3 h-3 text-obsidian-900" strokeWidth={2.5} />
            </div>
            <span
              className="text-[10px] tracking-[0.2em] font-semibold text-lime-500"
              style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
            >
              REELSCRIPT · SCORECARD
            </span>
          </div>
          <span
            className="text-[10px] tracking-[0.15em] text-obsidian-400"
            style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
          >
            {new Date().toISOString().slice(0, 10).replace(/-/g, '.')}
          </span>
        </div>

        {/* Verdict */}
        <div className="mb-6">
          <div
            className="text-[10px] tracking-[0.2em] text-obsidian-400 mb-2"
            style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
          >
            VERDICT · 影片評判
          </div>
          <div className="flex items-baseline gap-3">
            <span
              className="text-4xl"
              style={{ textShadow: '0 0 24px rgba(255,84,54,0.25)' }}
            >
              {v.emoji || '🔥'}
            </span>
            <h2
              className={`font-semibold tracking-tight leading-[1.05] text-lime-500 ${
                is916 ? 'text-[44px]' : 'text-[52px]'
              }`}
            >
              「{v.tag || '—'}」
            </h2>
          </div>
          {v.one_liner && (
            <p className={`text-white/90 mt-3 ${is916 ? 'text-sm' : 'text-base'}`}>
              {v.one_liner}
            </p>
          )}
        </div>

        <div className="h-px bg-white/10 mb-5" />

        {/* Key metrics grid */}
        <div className={`grid ${is916 ? 'grid-cols-2' : 'grid-cols-3'} gap-4 mb-5`}>
          <div>
            <div
              className="text-[9px] tracking-[0.2em] text-obsidian-400 mb-1"
              style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
            >
              COMPLETION
            </div>
            <div className="font-semibold tabular-nums text-white text-3xl">
              {fmt(m.completion_rate_pct)}
              <span className="text-lime-500 text-lg ml-0.5">%</span>
            </div>
          </div>
          <div>
            <div
              className="text-[9px] tracking-[0.2em] text-obsidian-400 mb-1"
              style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
            >
              AVG · WATCH
            </div>
            <div className="font-semibold tabular-nums text-white text-3xl">
              {fmt(m.avg_watch_time_sec)}
              <span className="text-lime-500 text-lg ml-0.5">s</span>
            </div>
          </div>
          <div>
            <div
              className="text-[9px] tracking-[0.2em] text-obsidian-400 mb-1"
              style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
            >
              NON · FOLLOWER
            </div>
            <div className="font-semibold tabular-nums text-white text-3xl">
              {fmt(m.non_follower_reach_pct)}
              <span className="text-lime-500 text-lg ml-0.5">%</span>
            </div>
          </div>
        </div>

        {/* Retention mini chart */}
        <div className="mb-5">
          <div
            className="flex items-center justify-between mb-1.5"
            style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
          >
            <span className="text-[9px] tracking-[0.2em] text-obsidian-400">
              RETENTION · CURVE
            </span>
            <span className="text-[9px] tracking-[0.15em] text-obsidian-400">
              0s ━━━━━━━━ {curve.length ? curve[curve.length - 1].t_sec : '—'}s
            </span>
          </div>
          <MiniRetentionChart curve={curve} height={is916 ? 80 : 72} />
        </div>

        {/* Radar + Demographics row */}
        <div className={`grid ${is916 ? 'grid-cols-[1fr_1fr]' : 'grid-cols-[180px_1fr]'} gap-5 mb-5 items-start`}>
          <div>
            <div
              className="text-[9px] tracking-[0.2em] text-obsidian-400 mb-1"
              style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
            >
              HEALTH · 5D
            </div>
            <MiniRadar scores={scores} size={is916 ? 170 : 180} />
          </div>
          <div>
            <div
              className="text-[9px] tracking-[0.2em] text-obsidian-400 mb-2"
              style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
            >
              TOP · REGIONS
            </div>
            <DemoBar rows={demo} />
          </div>
        </div>

        <div className="h-px bg-white/10 mb-4" />

        {/* AI insight */}
        <div className="flex-1">
          <div
            className="text-[9px] tracking-[0.2em] text-lime-500 mb-2"
            style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
          >
            AI · INSIGHT
          </div>
          <p className={`text-white leading-relaxed ${is916 ? 'text-[13px]' : 'text-sm'}`}>
            <span className="text-lime-500">✨ </span>
            {insights.highlight || '—'}
          </p>
          <p className={`text-white/80 mt-2 leading-relaxed ${is916 ? 'text-[13px]' : 'text-sm'}`}>
            <span className="text-yellow-400">⚠ </span>
            {insights.optimization || '—'}
          </p>
          <p className={`text-white/80 mt-2 leading-relaxed ${is916 ? 'text-[13px]' : 'text-sm'}`}>
            <span className="text-lime-300">→ </span>
            {insights.next_try || '—'}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 mt-3 border-t border-white/10">
          <div className="text-[10px] text-obsidian-300">
            {handle ? `@${handle}` : ''}
          </div>
          <div
            className="text-[9px] tracking-[0.25em] text-obsidian-400"
            style={{ fontFamily: '"SF Mono", ui-monospace, monospace' }}
          >
            REELSCRIPT.AI
          </div>
        </div>
      </div>
    </div>
  );
});

ShareableCard.displayName = 'ShareableCard';

export default ShareableCard;
